// Combobox contract types (D13, issue 57).
//
// They live beside Combobox.vue rather than inside it because `<script setup>`
// cannot carry module-level value exports, and `ComboboxRateLimited` is a class
// the consumer has to be able to construct.

export interface ComboboxOption {
  /** Stable identity — used as the option key and for equality against the selection. */
  value: string | number;
  /** The row's primary line. */
  label: string;
  /** Optional secondary line (region, country, …). */
  hint?: string;
  /** Whatever the consumer needs back on `select`; opaque here. */
  data?: unknown;
}

/**
 * Every string Combobox can render. The library carries no message catalogue, so
 * the consuming app supplies these already localized — that is what keeps the
 * nine locales in the app that owns them.
 */
export interface ComboboxLabels {
  /** Rendered while a search is in flight. */
  searching: string;
  /** Rendered when the query is shorter than `minQueryLength`. */
  typeMore: string;
  /** Rendered when the search came back empty — the "keep what you typed" path. */
  noMatch: string;
  /** Rendered when the search failed for any reason other than a rate limit. */
  error: string;
  /** Retry affordance next to `error`. */
  retry: string;
  /** Rendered while a rate limit is being waited out. */
  rateLimited: string;
  /**
   * Screen-reader announcement for a result count. A FUNCTION, not a template:
   * several of the product languages pluralize this (Polish alone has three
   * forms), and only the consumer's own i18n runtime can do that correctly.
   */
  resultsAnnouncement: (count: number) => string;
  /** Accessible name of the button that drops the current selection. */
  clear: string;
}

/**
 * A rate limit is NOT a failed search (ADR 0046).
 *
 * A debounced type-ahead is the control in the suite most likely to hit its own
 * limit — a fast typist on a slow network — and rendering that as an error, or
 * worse as "no matches", would silently push the user onto the keep-what-you-typed
 * path and lose them the row they were looking for. The consumer translates its
 * transport's 429 (`{ code: 'RATE_LIMITED', retryAfter }` + `Retry-After`, the
 * suite's one 429 shape) into this, and the control waits it out and retries the
 * same query by itself.
 */
export class ComboboxRateLimited extends Error {
  constructor(readonly retryAfterSeconds: number) {
    super('rate limited');
    this.name = 'ComboboxRateLimited';
  }
}
